/**
 * Cookie consent state, persisted to localStorage.
 *
 * Three possible states:
 *   - `null`       User hasn't chosen yet - banner should be visible.
 *   - `"accepted"` User opted into non-essential cookies (Sentry Session
 *                  Replay, any future analytics). Full tracking enabled.
 *   - `"essential"` User chose essential-only. Sentry error capture still
 *                   runs (necessary for service reliability) but Session
 *                   Replay and any optional trackers stay off.
 *
 * Default is privacy-preserving: until the user explicitly accepts, we
 * behave as if they chose "essential".
 */

const STORAGE_KEY = "whistll.cookie-consent.v1";
const CHANGE_EVENT = "whistll:cookie-consent-changed";

export type CookieConsent = "accepted" | "essential" | null;

export function getCookieConsent(): CookieConsent {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === "accepted" || raw === "essential" ? raw : null;
  } catch {
    // Private-mode Safari etc. - fall back to "no choice yet"
    return null;
  }
}

export function setCookieConsent(value: Exclude<CookieConsent, null>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: value }));
}

/**
 * Subscribe to consent changes (banner Accept/Essential-only clicks, or
 * manual changes in another tab via the storage event).
 */
export function subscribeToCookieConsent(
  listener: (value: CookieConsent) => void
): () => void {
  const onCustom = (e: Event) => {
    listener((e as CustomEvent<CookieConsent>).detail);
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener(getCookieConsent());
  };
  window.addEventListener(CHANGE_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

/** Has the user accepted non-essential cookies? */
export function hasFullConsent(): boolean {
  return getCookieConsent() === "accepted";
}
